<template>
  <c-modal
    v-if="isOpen"
    :title="`${filteredAvailableSessions?.length} Available Sessions`"
    size="full"
    @close="handleClose"
  >
    <template v-if="selectedGap" #header-subtitle>
      <div class="modal-timeframe">
        {{ selectedGap.timeDisplay }}
      </div>
    </template>

    <div class="modal-body">
      <div v-show="isFiltering" class="filtering-progress-bar"></div>

      <!-- Mobile Search and Filter Toggle Bar (shown < 1000px) -->
      <div v-if="showFilters" class="search-filter-bar mobile-only">
        <div class="search-input-wrapper">
          <i class="fa fa-search search-icon"></i>
          <input
            v-model="modalFilters.search"
            type="text"
            placeholder="Search sessions..."
            class="search-input"
            @input="handleSearchInput"
          />
          <button
            v-if="modalFilters.search"
            class="clear-search-btn"
            @click="clearSearch"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>

        <c-button
          theme="default"
          size="md"
          icon="fa-sliders"
          uppercase
          fill
          @click="toggleFilters"
        >
          Filters
          <span v-if="activeFiltersCount > 0" class="filter-badge">{{ activeFiltersCount }}</span>
          <svg v-show="filtersExpanded" key="icon-up" class="svg-inline--fa fa-chevron-up" aria-hidden="true" focusable="false" data-prefix="far" data-icon="chevron-up" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" data-fa-i2svg=""><path fill="currentColor" d="M239 111c9.4-9.4 24.6-9.4 33.9 0L465 303c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-175-175L81 337c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9L239 111z"></path></svg>
          <svg v-show="!filtersExpanded" key="icon-down" class="svg-inline--fa fa-chevron-down" aria-hidden="true" focusable="false" data-prefix="far" data-icon="chevron-down" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" data-fa-i2svg=""><path fill="currentColor" d="M239 401c9.4 9.4 24.6 9.4 33.9 0L465 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-175 175L81 175c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9L239 401z"></path></svg>
        </c-button>
      </div>

      <!-- Mobile Expandable Filters Panel (shown < 1000px) -->
      <transition name="filters-expand">
        <div v-if="filtersExpanded && showFilters" class="filters-panel mobile-only">
          <div class="filters-header">
            <h4>Filter Sessions</h4>
            <div>
              <c-button
                v-if="hasActiveFilters"
                theme="default"
                uppercase
                size="sm"
                icon="fa-close"
                @click="resetModalFilters"
              >Clear All</c-button>
              <c-button
                theme="default"
                uppercase
                size="sm"
                icon="fa-chevron-up"
                @click="toggleFilters"
              >Close</c-button>
            </div>
          </div>

          <agenda-filters
            :filters="modalFiltersStructure"
            :hide-search="true"
            @filter="handleModalFilterChange"
          />
        </div>
      </transition>

      <!-- Desktop Layout (shown >= 1000px) -->
      <div :class="showFilters ? 'modal-body-with-filters desktop-only' : 'modal-body'">
        <!-- Left column: Sessions List -->
        <div class="modal-sessions-column">
          <div v-if="filteredAvailableSessions.length === 0" class="no-sessions">
            <p>No available sessions match your filters</p>
            <c-button
              v-if="hasActiveFilters"
              theme="default"
              uppercase
              size="sm"
              fill
              icon="fa-filter-slash"
              @click="resetModalFilters"
            >
              Reset Filters
            </c-button>
          </div>

          <div v-else class="sessions-list">
            <session-item
              v-for="session in filteredAvailableSessions"
              :key="session.id"
              :session="session"
              :show-indicator="showSchedulerIndicator"
              @add="updateScheduler"
            />
          </div>
        </div>

        <!-- Right column: Filters (Desktop) -->
        <div v-if="showFilters" class="modal-filters-column">
          <div class="agenda-filter">
            <div class="agenda-filter__top">
              <agenda-filter-top :total="filteredAvailableSessions.length" @reset="resetModalFilters" />
            </div>

            <agenda-filters :filters="modalFiltersStructure" @filter="handleModalFilterChange" />
          </div>
        </div>
      </div>

      <!-- Mobile Sessions Container (shown < 1000px) -->
      <div class="sessions-container mobile-only">
        <div v-if="filteredAvailableSessions.length === 0" class="no-sessions">
          <p>No available sessions match your filters</p>
          <c-button
            v-if="hasActiveFilters"
            theme="default"
            uppercase
            size="sm"
            fill
            icon="fa-filter-slash"
            @click="resetModalFilters"
          >
            Reset Filters
          </c-button>
        </div>

        <div v-else class="sessions-list">
          <session-item
            v-for="session in filteredAvailableSessions"
            :key="session.id"
            :session="session"
            :show-indicator="showSchedulerIndicator"
            @add="updateScheduler"
          />
        </div>
      </div>
    </div>
  </c-modal>
</template>

<script>
import agendaAPI from "../../../api/agendaAPI";
import AgendaFilterTop from "../../../components/agenda/agenda-filter-top";
import AgendaFilters from "../../../components/agenda/agenda-filters";
import SessionItem from "./session-item";
import { debounce } from "../../../../global-declare-components/helpers/debounce";

// Constants
const FILTER_DEBOUNCE_DELAY = 300;
const LOADER_DISPLAY_DELAY = 50;

export default {
  name: 'GapSessionsModal',
  components: {
    AgendaFilterTop,
    AgendaFilters,
    SessionItem
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    selectedGap: {
      type: Object,
      default: null
    },
    availableSessions: {
      type: Array,
      default: () => []
    },
    attendeeId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      showSchedulerIndicator: false,
      co_occurring: null,
      filtersExpanded: false,
      isFiltering: false,

      modalFilters: {
        search: '',
        formats: [],
        presenters: [],
        topics: [],
        tracks: [],
        types: [],
        visiting_types: []
      }
    };
  },
  computed: {
    modalFiltersStructure() {
      return {
        search: this.modalFilters.search,
        types: this.typeFilters,
        visiting_types: this.visitingTypeFilters,
        formats: this.formatFilters,
        tracks: this.trackFilters,
        topics: this.topicFilters,
        presenters: this.presenterFilters
      };
    },

    showFilters() {
      return this.availableSessions.length > 1;
    },

    formatFilters() {
      return this.buildFilterOptions(
        this.getCountMap(this.availableSessions, 'format'),
        'formats'
      );
    },

    presenterFilters() {
      const presenterMap = new Map();
      this.availableSessions.forEach(session => {
        if (session.presenters?.length) {
          session.presenters.forEach(presenter => {
            const existing = presenterMap.get(presenter.id) || { name: presenter.name, count: 0 };
            presenterMap.set(presenter.id, { name: presenter.name, count: existing.count + 1 });
          });
        }
      });

      return Array.from(presenterMap.entries())
        .map(([id, data]) => ({
          name: data.name,
          value: id,
          count: data.count,
          is_checked: this.modalFilters.presenters.includes(id)
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },

    topicFilters() {
      const topicMap = new Map();
      this.availableSessions.forEach(session => {
        if (session.topics?.length) {
          session.topics.forEach(topic => {
            if (!topicMap.has(topic.id)) {
              topicMap.set(topic.id, {
                id: topic.id,
                title: topic.title,
                icon: topic.icon || 'fa-tag',
                count: 0
              });
            }
            topicMap.get(topic.id).count++;
          });
        }
      });

      return Array.from(topicMap.entries())
        .map(([id, data]) => ({
          name: data.title,
          value: id,
          count: data.count,
          icon: data.icon,
          is_checked: this.modalFilters.topics.includes(id)
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },

    trackFilters() {
      const trackMap = new Map();
      this.availableSessions.forEach(session => {
        if (session.tracks?.length) {
          session.tracks.forEach(track => {
            const existing = trackMap.get(track.id) || { name: track.name, color: track.color, count: 0 };
            trackMap.set(track.id, { name: track.name, color: track.color, count: existing.count + 1 });
          });
        }
      });

      return Array.from(trackMap.entries())
        .map(([id, data]) => ({
          name: data.name,
          value: id,
          count: data.count,
          color: data.color,
          is_checked: this.modalFilters.tracks.includes(id)
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },

    typeFilters() {
      const typeMap = new Map();
      this.availableSessions.forEach(session => {
        const typeValue = session.is_educational ? 1 : 0;
        typeMap.set(typeValue, (typeMap.get(typeValue) || 0) + 1);
      });

      return Array.from(typeMap.entries())
        .map(([value, count]) => ({
          name: value === 1 ? 'Educational' : 'Non Educational',
          value: value,
          count: count,
          is_checked: this.modalFilters.types.includes(value)
        }))
        .sort((a, b) => b.value - a.value);
    },

    visitingTypeFilters() {
      const visitingTypeMap = new Map();
      this.availableSessions.forEach(session => {
        if (session.visiting_type !== undefined && session.visiting_type !== null) {
          visitingTypeMap.set(session.visiting_type, (visitingTypeMap.get(session.visiting_type) || 0) + 1);
        }
      });

      const labels = ['In-person', 'Virtual', 'Hybrid'];
      return Array.from(visitingTypeMap.entries())
        .map(([value, count]) => ({
          name: labels[value] || 'Unknown',
          value: value,
          count: count,
          is_checked: this.modalFilters.visiting_types.includes(value)
        }))
        .sort((a, b) => a.value - b.value);
    },

    filteredAvailableSessions() {
      let filtered = this.availableSessions;

      filtered = this.applySearchFilter(filtered);
      filtered = this.applySimpleArrayFilter(filtered, 'formats', 'format');
      filtered = this.applyNestedArrayFilter(filtered, 'presenters', 'presenters', 'id');
      filtered = this.applyNestedArrayFilter(filtered, 'topics', 'topics', 'id');
      filtered = this.applyNestedArrayFilter(filtered, 'tracks', 'tracks', 'id');
      filtered = this.applyTypeFilter(filtered);
      filtered = this.applySimpleArrayFilter(filtered, 'visiting_types', 'visiting_type');

      return filtered;
    },

    hasActiveFilters() {
      return this.activeFiltersCount > 0 || this.modalFilters.search.trim() !== '';
    },

    activeFiltersCount() {
      return Object.entries(this.modalFilters)
        .filter(([key, value]) => Array.isArray(value))
        .reduce((sum, [, arr]) => sum + arr.length, 0);
    }
  },
  watch: {
    isOpen: {
      immediate: false,
      handler(newVal) {
        if (newVal) {
          this.resetModalFilters();
          this.filtersExpanded = false;
          this.isFiltering = false;
        }
      }
    }
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },

    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },

    // Helper: Show filtering loader
    showFilteringLoader(callback, delay = LOADER_DISPLAY_DELAY) {
      this.isFiltering = true;
      this.$nextTick(() => {
        setTimeout(() => {
          callback();
          this.$nextTick(() => {
            this.isFiltering = false;
          });
        }, delay);
      });
    },

    // Helper: Build filter options from count map
    buildFilterOptions(countMap, filterKey) {
      return Array.from(countMap.entries())
        .map(([value, count]) => ({
          name: value,
          value: value,
          count: count,
          is_checked: this.modalFilters[filterKey].includes(value)
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },

    // Helper: Get count map for simple fields
    getCountMap(sessions, field) {
      const map = new Map();
      sessions.forEach(session => {
        if (session[field]) {
          map.set(session[field], (map.get(session[field]) || 0) + 1);
        }
      });
      return map;
    },

    // Filter helpers
    applySearchFilter(sessions) {
      if (!this.modalFilters.search.trim()) return sessions;
      const query = this.modalFilters.search.toLowerCase().trim();
      return sessions.filter(s => s.name?.toLowerCase().includes(query));
    },

    applySimpleArrayFilter(sessions, filterKey, sessionKey) {
      if (this.modalFilters[filterKey].length === 0) return sessions;
      return sessions.filter(s =>
        s[sessionKey] !== undefined &&
        s[sessionKey] !== null &&
        this.modalFilters[filterKey].includes(s[sessionKey])
      );
    },

    applyNestedArrayFilter(sessions, filterKey, sessionKey, idKey) {
      if (this.modalFilters[filterKey].length === 0) return sessions;
      return sessions.filter(s =>
        s[sessionKey]?.some(item => this.modalFilters[filterKey].includes(item[idKey]))
      );
    },

    applyTypeFilter(sessions) {
      if (this.modalFilters.types.length === 0) return sessions;
      return sessions.filter(s => {
        const sessionType = s.is_educational ? 1 : 0;
        return this.modalFilters.types.includes(sessionType);
      });
    },

    handleSearchInput() {
      this.isFiltering = true;
      this.$nextTick(() => {
        setTimeout(() => {
          this.debouncedSearchComplete();
        }, LOADER_DISPLAY_DELAY);
      });
    },

    debouncedSearchComplete: debounce(function() {
      this.$nextTick(() => {
        this.isFiltering = false;
      });
    }, FILTER_DEBOUNCE_DELAY),

    clearSearch() {
      this.showFilteringLoader(() => {
        this.modalFilters.search = '';
      });
    },

    handleModalFilterChange(filters) {
      this.isFiltering = true;
      this.$nextTick(() => {
        setTimeout(() => {
          this.debouncedFilterUpdate(filters);
        }, LOADER_DISPLAY_DELAY);
      });
    },

    debouncedFilterUpdate: debounce(function(filters) {
      this.modalFilters = {
        search: filters.search || '',
        formats: filters.formats?.filter(f => f.is_checked).map(f => f.value) || [],
        presenters: filters.presenters?.filter(p => p.is_checked).map(p => p.value) || [],
        topics: filters.topics?.filter(t => t.is_checked).map(t => t.value) || [],
        tracks: filters.tracks?.filter(t => t.is_checked).map(t => t.value) || [],
        types: filters.types?.filter(t => t.is_checked).map(t => t.value) || [],
        visiting_types: filters.visiting_types?.filter(vt => vt.is_checked).map(vt => vt.value) || []
      };

      this.$nextTick(() => {
        this.isFiltering = false;
      });
    }, FILTER_DEBOUNCE_DELAY),

    resetModalFilters() {
      this.showFilteringLoader(() => {
        this.modalFilters = {
          search: '',
          formats: [],
          presenters: [],
          topics: [],
          tracks: [],
          types: [],
          visiting_types: []
        };
      });
    },

    updateScheduler(session, params = {}) {
      if (!session?.id) {
        console.error('Invalid session data');
        return;
      }

      this.showSchedulerIndicator = true;

      if (session?.buttons?.my_schedule?.completed) {
        agendaAPI.removeFromScheduler(this.attendeeId, session.id)
          .then(() => {
            session.buttons.my_schedule.completed = false;
            session.my_agenda = false;

            this.$store.dispatch("agendaStore/updateFlatSessionById", {
              id: session.id,
              session: session
            });
            this.$store.dispatch('attendeeStore/updateMyAgendaCount', { count: -1 });

            this.$emit('session-updated', session);
            this.handleClose();
          })
          .catch((error) => {
            console.error('Error removing from schedule:', error);
          })
          .finally(() => {
            this.showSchedulerIndicator = false;
          });
      } else {
        agendaAPI.addToScheduler(this.attendeeId, session.id, params)
          .then(() => {
            this.co_occurring = null;
            session.buttons.my_schedule.completed = true;
            session.my_agenda = true;

            this.$store.dispatch("agendaStore/updateFlatSessionById", {
              id: session.id,
              session: session
            });
            this.$store.dispatch('attendeeStore/updateMyAgendaCount', { count: 1 });

            this.$emit('session-updated', session);
            this.handleClose();
          })
          .catch((error) => {
            if (error.response?.status === 422) {
              this.co_occurring = error.response.data;
            }
            console.error('Error adding to schedule:', error);
          })
          .finally(() => {
            this.showSchedulerIndicator = false;
          });
      }
    }
  }
};
</script>

<style scoped lang="scss">
.modal-timeframe {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  margin-top: 4px;
}

.modal-body {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

.desktop-only {
  display: none;

  @media (min-width: 1000px) {
    display: block;
  }
}

.mobile-only {
  display: block;

  @media (min-width: 1000px) {
    display: none;
  }
}

.search-filter-bar {
  display: flex;
  gap: var(--attendee-gap-sm);
  align-items: center;
  padding-bottom: var(--attendee-gap-sm);

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;

  .search-icon {
    position: absolute;
    left: 12px;
    color: #9ca3af;
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    padding: 10px 40px 10px 38px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: var(--attendee-primary-color, #4F46E5);
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }

    &::placeholder {
      color: #9ca3af;
    }
  }

  .clear-search-btn {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    padding: 6px;
    cursor: pointer;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
      background: #f3f4f6;
      color: #6b7280;
    }
  }
}

.filter-badge {
  background: var(--attendee-primary-color, #f66);
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
}

.filters-panel {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: var(--attendee-gap-md);
  margin-bottom: var(--attendee-gap-sm);
}

.filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--attendee-gap-md);
  padding-bottom: var(--attendee-gap-sm);
  border-bottom: 1px solid #e5e7eb;

  h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }
}

.filters-expand-enter-active,
.filters-expand-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  overflow: hidden;
}

.filters-expand-enter-from,
.filters-expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-bottom: 0;
}

.modal-body-with-filters {
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: var(--attendee-gap-sm);
  height: 100%;
}

.modal-sessions-column {
  overflow-y: auto;
  padding-right: var(--attendee-gap-sm);
}

.modal-filters-column {
  border: 1px solid var(--attendee-border-color);
  border-radius: var(--attendee-card-border-radius);
  overflow-y: auto;
}

.agenda-filter {
  background: transparent;
  padding: var(--attendee-gap-lg);
  padding-bottom: 80px;

  &__top {
    border-bottom: 1px solid var(--attendee-border-color);
    padding-bottom: var(--attendee-gap-md);
    margin-bottom: var(--attendee-gap-md);
  }
}

.sessions-container {
  flex: 1;
  overflow-y: auto;
}

.no-sessions {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  gap: var(--attendee-gap-md);
  align-items: center;

  p {
    font-size: 16px;
    margin: 0;
  }
}

.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.available-session-item {
  display: flex;
  gap: 15px;
  padding: 15px;
  border: 1px solid #e5e7eb;
  border-radius: var(--attendee-card-border-radius);
  cursor: pointer;
  transition: all 0.2s;
  background: white;

  &:hover {
    border-color: var(--attendee-primary-color, #4F46E5);
    box-shadow: 0 2px 8px rgba(79, 70, 229, 0.1);
  }
}

.session-info {
  flex: 1;
}

.session-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.session-title {
  font-weight: 600;
  color: var(--attendee-text-primary-color);
  font-size: 15px;
  line-height: 1.4;
}

.session-time-badge {
  font-size: 12px;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 4px;
  color: #4b5563;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.session-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}

.meta-item {
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    opacity: 0.7;
  }
}

.session-presenters {
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    opacity: 0.7;
  }
}

@media (max-width: 900px) {
  .available-session-item {
    flex-direction: column;
  }
}

.filtering-progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.05);
  z-index: 1000;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 40%;
    height: 100%;
    background: var(--attendee-primary-color, #4F46E5);
    animation: progress-slide 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }
}

@keyframes progress-slide {
  0% {
    left: -40%;
  }
  100% {
    left: 100%;
  }
}
</style>
